import {
  Building2,
  ClipboardList,
  Compass,
  FileSignature,
  Handshake,
  Key,
  Package,
  Scale,
  ShieldCheck,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { UserRole } from "@/contexts/AuthContext";
import { AREA_ROLES } from "@/utils/errandPath";

/**
 * What the walkthrough's second screen says a reader can do here, chosen by the
 * job they were given.
 *
 * **A new admin used to be handed the colleague's introduction.** Three screens
 * about finding and requesting a tool, and nothing about the queue they now
 * own, on the morning they were given the role. The remedy is this screen and
 * not a second walkthrough: the welcome and the tick list apply to everybody
 * (every role is also an employee here), so only the middle screen is about the
 * job, and one frame keeps one gate, one bookmark and one way back in.
 *
 * **Every card names something the app has, and `about` is how that is
 * enforced.** The inherited fourth card described reading tips shared across
 * the group, which is a surface this app does not have — the one thing on that
 * screen a new joiner could go looking for and fail to find. `about` is the
 * path a card is talking about, and `introCards.test.ts` holds each one to a
 * route that exists and that the role can actually reach. `null` is for a card
 * that describes a property rather than a place; it is spelled out so that
 * leaving it off is impossible.
 *
 * The cards describe rather than navigate, as they always have: the walkthrough
 * sits in front of the app, and a card that went somewhere would leave it
 * half-finished.
 */
export interface IntroCard {
  id: string;
  icon: LucideIcon;
  /** Key under `onboarding.` in the `endUser` bundle. */
  title: string;
  description: string;
  /** The page this card is about.
   *
   *  **Required, and it was optional.** One card carried `null` for "describes
   *  a property rather than a place" — and that card was the one claiming every
   *  tool in the catalogue has been reviewed, which the catalogue's own page
   *  contradicts in as many words. The exemption and the false sentence were
   *  the same hole: a card that names no page is a card no test can hold to
   *  one. Every card now points at the page a reader would go to. */
  about: string;
}

/** Every role is also an employee here (ADR 0001), and an admin's own tools are
 *  the least discoverable thing about the app for them: their shell is the
 *  admin one, and the way across is the account menu. */
const OWN_TOOLS: IntroCard = {
  id: "own-tools",
  icon: Compass,
  title: "onboarding.ownToolsTitle",
  description: "onboarding.ownToolsDesc",
  about: "/end-user/my-tools",
};

const END_USER_CARDS: readonly IntroCard[] = [
  {
    id: "explore",
    icon: Compass,
    title: "onboarding.exploreTitle",
    description: "onboarding.exploreDesc",
    about: "/end-user/tools",
  },
  {
    id: "request",
    icon: Key,
    title: "onboarding.requestTitle",
    description: "onboarding.requestDesc",
    about: "/end-user/request-access",
  },
  {
    id: "compliance",
    icon: ShieldCheck,
    title: "onboarding.complianceTitle",
    description: "onboarding.complianceDesc",
    // The catalogue is where a reader sees this: every entry carries its own
    // unit's position, and the page partitions them by it.
    about: "/end-user/tools",
  },
  {
    id: "track",
    icon: ClipboardList,
    title: "onboarding.trackTitle",
    description: "onboarding.trackDesc",
    about: "/end-user/my-requests",
  },
];

const BU_ADMIN_CARDS: readonly IntroCard[] = [
  {
    id: "bu-queue",
    icon: ClipboardList,
    title: "onboarding.buQueueTitle",
    description: "onboarding.buQueueDesc",
    about: "/bu-admin/requests",
  },
  {
    id: "bu-catalogue",
    icon: Package,
    title: "onboarding.buCatalogueTitle",
    description: "onboarding.buCatalogueDesc",
    about: "/bu-admin/tools",
  },
  {
    id: "bu-people",
    icon: Users,
    title: "onboarding.buPeopleTitle",
    description: "onboarding.buPeopleDesc",
    about: "/bu-admin/users",
  },
  OWN_TOOLS,
];

const SUPER_ADMIN_CARDS: readonly IntroCard[] = [
  {
    id: "group-catalogue",
    icon: Package,
    title: "onboarding.groupCatalogueTitle",
    description: "onboarding.groupCatalogueDesc",
    about: "/admin/catalogue",
  },
  {
    id: "business-units",
    icon: Building2,
    title: "onboarding.businessUnitsTitle",
    description: "onboarding.businessUnitsDesc",
    about: "/admin/business-units",
  },
  {
    id: "administrators",
    icon: ShieldCheck,
    title: "onboarding.administratorsTitle",
    description: "onboarding.administratorsDesc",
    about: "/admin/administrators",
  },
  OWN_TOOLS,
];

const GROUP_LEGAL_CARDS: readonly IntroCard[] = [
  {
    id: "legal-pending",
    icon: Scale,
    title: "onboarding.legalPendingTitle",
    description: "onboarding.legalPendingDesc",
    about: "/group-legal/pending",
  },
  {
    id: "legal-negotiation",
    icon: Handshake,
    title: "onboarding.legalNegotiationTitle",
    description: "onboarding.legalNegotiationDesc",
    about: "/group-legal/negotiation",
  },
  {
    id: "legal-contracted",
    icon: FileSignature,
    title: "onboarding.legalContractedTitle",
    description: "onboarding.legalContractedDesc",
    about: "/group-legal/contracted",
  },
  OWN_TOOLS,
];

/**
 * The roles the screens route on, derived from the areas rather than listed
 * again.
 *
 * Derived from the areas rather than written as `Record<UserRole, …>`, so a
 * role the vocabulary gains before any area admits it is not handed an
 * introduction to screens that do not exist for it.
 */
type ScreenRole = (typeof AREA_ROLES)[keyof typeof AREA_ROLES][number];

/** A `Record` over those, so a fifth *screen* role is a compile error rather
 *  than a reader who quietly gets somebody else's introduction. */
const CARDS_BY_ROLE: Record<ScreenRole, readonly IntroCard[]> = {
  end_user: END_USER_CARDS,
  bu_admin: BU_ADMIN_CARDS,
  super_admin: SUPER_ADMIN_CARDS,
  group_legal: GROUP_LEGAL_CARDS,
};

export const INTRO_CARD_ROLES = Object.keys(CARDS_BY_ROLE) as ScreenRole[];

/**
 * Is there an introduction to show this reader at all?
 *
 * **What decides whether the gate marches somebody through the walkthrough.**
 * It used to be `role === "end_user"`, inherited from the port and never
 * examined, which meant a new admin was not handed the colleague's
 * introduction: they were handed none. This asks the question the screen can
 * answer, so a role with cards gets them and a role without screens is left
 * alone.
 */
export function hasIntroduction(role: UserRole | null | undefined): boolean {
  return role !== null && role !== undefined && role in CARDS_BY_ROLE;
}

/** What the gate knows when it decides whether to introduce somebody. */
export interface IntroductionCheck {
  /** Has the profile subscription produced its first answer, either way? */
  profileResolved: boolean;
  /** Did that subscription fail rather than answer? */
  profileReadFailed: boolean;
  /** The profile's flag, which is a `?? false` fallback until it arrives. */
  onboardingCompleted: boolean;
  role: UserRole | null | undefined;
  isImpersonating: boolean;
}

/**
 * Should the gate send this reader into the walkthrough?
 *
 * Extracted from the gate because it is five terms and two of them are about
 * *evidence* rather than about the reader:
 *
 * - **A read that failed is not a reader who has never been introduced.** The
 *   error path resolves the subscription as well, and `onboardingCompleted` is
 *   a `?? false` fallback, so a transient `users/{uid}` error read as "not
 *   onboarded" and sent an established reader into the walkthrough with
 *   `replace: true`, taking the page they were on with it. `roleMissing` in
 *   `AuthContext` refuses to decide without the same guard.
 * - **Never while impersonating**, because `role` and `onboardingCompleted` are
 *   the target's, while the writes the walkthrough makes are the admin's own.
 */
export function shouldIntroduce({
  profileResolved,
  profileReadFailed,
  onboardingCompleted,
  role,
  isImpersonating,
}: IntroductionCheck): boolean {
  if (!profileResolved || profileReadFailed) return false;
  if (onboardingCompleted || isImpersonating) return false;
  return hasIntroduction(role);
}

/**
 * The cards for a reader's role.
 *
 * Two readers fall back to the employee's cards. One has no role at all, and
 * never reaches this page: the gate sends them to `/no-access` first. The other
 * holds one of the four roles no area admits, and cannot exist today either —
 * nothing assigns those, in the interface or in the backend, which refuses
 * them. If one is ever made assignable, this fallback is the least wrong
 * answer and not a correct one, and it is the smaller of that change's
 * problems: `/home` sends such a reader to `/end-user/dashboard`, which the
 * area guard refuses, which returns them to `/home`.
 */
export function introCardsFor(
  role: UserRole | null | undefined,
): readonly IntroCard[] {
  return role && role in CARDS_BY_ROLE
    ? CARDS_BY_ROLE[role as ScreenRole]
    : END_USER_CARDS;
}
